import { Box, Typography, CircularProgress, Button, useTheme, IconButton, Collapse, Select, MenuItem, } from '@mui/material'
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react'

import AddFeaturePermissionDialog from '../../components/admin/control-panel/AddFeaturePermissionDialog'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import { useOrganizationStore } from '../../store/organization.store'
import BlockMessage from '../../components/common/BlockMessage'
import { useUserStore } from '../../store/user.store'
import { Icons } from '../../utils/utilities'
import api from '../../service/api'
import CodeButton from '../../components/common/CodeButton'
import CustomSwitch from '../../components/common/CustomSwitch'
import CustomTabs from '../../components/common/CustomTabs'
import { toast } from 'react-toastify'
import noRulesImg from '../../assets/no-resume-templates.svg'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import DepartmentOrBranchPermission from '../../components/admin/control-panel/DepartmentOrBranchPermission'
import FeatureInstituteCard from '../../components/admin/control-panel/FeatureInstituteCard'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import CustomChip from '../../components/common/CustomChip'

const FeatureBlock = memo( ( { feature, selected, handleFeatureSelect } ) => {

    const [loadingFeature, setLoadingFeature] = useState( false )

    const { palette, border, layout } = useTheme()

    const selectFeature = async () => {
        setLoadingFeature( true )
        await handleFeatureSelect( feature.key )
        setLoadingFeature( false )
    }

    return (
        <Box
            component="button"
            borderRadius={layout.borderRadius}
            key={feature.key}
            border={border[1]}
            borderColor={selected ? palette.primary.light : palette.borderColor}
            onClick={selectFeature}
            padding="12px 15px"
            sx={{
                cursor: "pointer",
            }}
            bgcolor={palette.contentBg}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            color={palette.text.secondary}
        >
            <Box>
                <Typography color={selected ? 'textPrimary' : "textSecondary"} margin="0" lineHeight={1.2} component="p" textTransform="capitalize" variant='subtitle2' fontWeight={selected ? "600" : "400"}>
                    {feature.name}
                </Typography>
            </Box>
            <Typography color={selected ? "customThemeColor" : "GrayText"} >{loadingFeature ? <CircularProgress size={14} /> : Icons.default.CheckCircleIconOutlined}</Typography>
        </Box>
    )
} )

const FeaturePermissions = observer( () => {
    const [searchParams, setSearchParams] = useSearchParams( { selectedFeature: '' } )
    const selectedFeatureKey = searchParams.get( 'selectedFeature' )

    const [fetchingDepartmentsAndBranches, setFetchingDepartmentsAndBranches] = useState( true )
    const [deleteConfirmationDialogState, setDeleteConfirmationDialogState] = useState( false )
    const [selectedFeatureDetails, setSelectedFeatureDetails] = useState( null )
    const [selectedFeature, setSelectedFeature] = useState( selectedFeatureKey || null )
    const [departmentsAndBranches, setDepartmentsAndBranches] = useState( {} )
    const [fetchingPermissions, setFetchingPermissions] = useState( false )
    const [allPermissions, setFeaturePermissions] = useState( {} )
    const [addDialogState, setAddDialogState] = useState( false )
    const [hasPermission, setHasPermission] = useState( true )
    const [deleting, setDeleting] = useState( false )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const OrganizationStore = useOrganizationStore()
    const UserStore = useUserStore()
    const { layout, border, palette, shadows } = useTheme()

    const setSearchParam = ( key, val ) => {
        setSearchParams( prev => {
            prev.set( key, val )
            return prev
        }, { replace: true } )
    }

    const copyFeatureKey = () => {
        navigator.clipboard.writeText( selectedFeatureDetails.key )
            .then( () => {
                toast( `Feature key copied to clipboard` )
            } )
            .catch( err => {
                toast( 'Failed to copy: ', err )
            } )
    }

    const refetchUpdatedPermissions = async () => {
        try {
            const { data: permissionsData } = await api.featurePermissions.fetchPermissions()

            setFeaturePermissions( permissionsData.data )
            const features = Object.keys( permissionsData?.data || {} )
            if ( features.length > 0 ) {
                setSelectedFeature( features[0] )
            } else {
                setSelectedFeature( null )
            }
            setError( null )
        } catch ( err ) {
            console.error( "Failed to fetch permissions", err )
            setError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching permissions" } )
        }
    }

    // 2. Fetch institute-wise data for the SELECTED feature
    const fetchAllPermissions = useCallback( async () => {
        setFetchingPermissions( true )
        try {

            const requests = [
                api.featurePermissions.fetchPermissions(),
                api.featurePermissions.fetchInstituteDepartmentAndBranches()
            ]
            if ( OrganizationStore.getOrganization?.length === 0 )
                requests.push( OrganizationStore.fetchOrganization() )

            const [permissionsRes, deptAndBranchRes] = await Promise.all( requests )

            const { data: deptAndBranchData } = deptAndBranchRes
            const { data: permissionsData } = permissionsRes

            setDepartmentsAndBranches( deptAndBranchData.data )
            setFeaturePermissions( permissionsData.data )
            const features = Object.keys( permissionsData?.data || {} )
            if ( features.length > 0 )
                setSelectedFeature( features[0] )
            else
                setSelectedFeature( null )
            setError( null )
        } catch ( err ) {
            console.error( "Failed to fetch permissions", err )
            setError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching permissions" } )
        } finally {
            setLoading( false )
            setFetchingPermissions( false )
        }
    }, [OrganizationStore] )

    const handleFeatureSelect = feature => {
        setSelectedFeature( feature )
        setSearchParam( 'selectedFeature', feature )
    }

    const deleteSelectedFeature = async () => {
        try {
            setDeleting( true )
            await api.featurePermissions.deleteFeaturePermissions( selectedFeature )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while deleting the feature permission rules." )
        } finally {
            setDeleting( false )
        }
    }

    const features = Object.keys( allPermissions ).map( feature => {
        const details = {}
        details.name = allPermissions[feature].name
        details.key = feature
        return details
    } )

    const featureInstitutesData = useMemo( () => {
        if ( !allPermissions[selectedFeature]?.institutes ) return []
        const institutes = Object.values( allPermissions[selectedFeature]?.institutes )
        return institutes.sort( ( a, b ) => a.id - b.id )

    }, [allPermissions, selectedFeature] )

    // Initial Load
    useEffect( () => {
        // Permission check (using your UserStore logic)
        if ( UserStore.getUser.user_role === 'SUBADMIN' && !UserStore.getUser.user_permissions.control_panel ) {
            setHasPermission( false )
        } else {
            fetchAllPermissions()
        }
    }, [OrganizationStore, UserStore, fetchAllPermissions] )

    useEffect( () => {
        setSelectedFeatureDetails( features.find( fea => fea.key === selectedFeature ) )
    }, [selectedFeature, features] )


    return (
        hasPermission ? (

            <Box display="flex" flexDirection="column" overflow="auto" flexGrow={1}>

                <ConfirmDialog waiting={deleting} actionName='Delete' message={`All the associated rules will be deleted and the default permission will be set to false!`} secondaryConfirmationText={selectedFeature} enableSecondaryConfirmation confirmAction={deleteSelectedFeature} cancelAction={() => setDeleteConfirmationDialogState( false )} status={deleteConfirmationDialogState} />

                {/* Header */}
                <Box flexDirection={{ md: "row", xs: "column" }} gap={layout.cardsGap} alignItems={{ md: "center", xs: "stretch" }} display="flex" padding={layout.pagePadding} justifyContent="space-between">
                    <Box>
                        <Typography variant="h1" fontWeight="600" fontSize="16px">Module Permissions Control</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>Manage feature visibility across institutes and departments</Typography>
                    </Box>
                    <Button
                        onClick={() => setAddDialogState( true )}
                        startIcon={Icons.default.AddBox}
                        variant="contained"
                        disableElevation
                    >
                        Initialize New Feature
                    </Button>
                </Box>

                {/* Main Content Area */}
                {!loading && !error && features.length > 0 && <Box paddingLeft={layout.pagePadding} flexGrow={1} display="flex" flexDirection={{ md: "row", xs: "column" }} minHeight={0}>

                    {/* Left Sidebar: Feature List */}
                    <Box
                        borderRadius={layout.borderRadius}
                        border={border[1]}
                        gap={layout.cardsGap}
                        boxShadow={shadows[1]}
                        minWidth={{ md: "280px", xs: "100%" }}
                        display={{ md: "flex", xs: "none" }}
                        flexDirection="column"
                        padding="5px"
                        bgcolor={palette.background.paper}
                    >
                        <Typography padding="10px" variant="caption" fontWeight="bold" color="textSecondary">FEATURES</Typography>
                        {features.map( item => (
                            <FeatureBlock key={item.key} handleFeatureSelect={handleFeatureSelect} feature={item} selected={selectedFeature === item.key} />
                        ) )}
                    </Box>

                    {/* Right Panel: Institute List & Toggles */}
                    <Box
                        flexGrow={1}
                        display="flex"
                        flexDirection="column"
                        gap={layout.cardsGap}
                        paddingTop={{ xs: layout.cardsGap, md: "0" }}
                        paddingBottom={layout.cardsGap}
                        paddingLeft={{ md: layout.cardsGap, xs: "0" }}
                        paddingRight={layout.cardsGap}
                        className="custom-scrollbar"
                        overflow="auto"
                    >
                        {selectedFeatureDetails && <Box bgcolor={palette.contentBg} borderRadius={layout.borderRadius} padding="10px" display="flex" alignItems="center" flexWrap="wrap" justifyContent="space-between" >
                            <Box display={{ md: "block", xs: "none" }}>
                                <Typography variant='subtitle2' component="h1">{selectedFeatureDetails?.name}</Typography>
                                <CustomChip value={selectedFeatureDetails?.key} />
                            </Box>
                            <Box display={{ xs: "block", md: "none" }}>
                                <Select slotProps={{ input: { name: "feature-select", id: "feature-select" } }} fullWidth size='small' value={selectedFeature} onChange={event => handleFeatureSelect( event.target.value )} >
                                    {features.map( item => ( <MenuItem value={item.key} key={item.key}>
                                        {item.name}
                                    </MenuItem> ) )}
                                </Select>
                            </Box>
                            {/* <Box>
                                <Typography color='error' display="flex" alignItems="center" gap="5px" variant='h6' fontSize="16px" component="h3"> {Icons.default.Warning} Delete {features.find( fea => fea.key === selectedFeature ).name} permission rules</Typography>
                                <Typography color='textSecondary' fontSize="12px" variant='subtitle2' component="p"> All the permissions will be removed and the default permissions will be set to false for this feature.</Typography>
                            </Box> */}
                            <Box display="flex" alignItems="center" gap={layout.cardsGap}>
                                <Button color='greyed' sx={{ ":hover": { color: "secondary.main" } }} disableElevation size='small' onClick={copyFeatureKey} startIcon={Icons.default.Copy}>Copy Key</Button>
                                <Button color='greyed' sx={{ ":hover": { color: "error.main" } }} disableElevation size='small' onClick={() => setDeleteConfirmationDialogState( true )} startIcon={Icons.default.DeleteForever}>Delete Feature</Button>
                            </Box>
                        </Box>}
                        <Box flexGrow={1} overflow="auto" display="flex" flexDirection="column" gap={layout.cardsGap} className="custom-scrollbar" >
                            {(
                                featureInstitutesData.length > 0 ? ( featureInstitutesData.map( instRecord => {
                                    return (
                                        <FeatureInstituteCard
                                            featureKey={selectedFeature}
                                            departmentsAndBranches={departmentsAndBranches}
                                            key={instRecord.id}
                                            record={instRecord}
                                            refresh={refetchUpdatedPermissions}
                                        />
                                    )
                                } )
                                ) : (
                                    <Box padding="40px" textAlign="center" border={border[1]} borderRadius={layout.borderRadius} sx={{ borderStyle: 'dashed' }}>
                                        <Typography variant="subtitle2">No institutes mapped to this feature.</Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    </Box>
                </Box>}

                {!loading && !error && features.length === 0 && <Box margin={{ sm: `0 20px 20px 20px`, xs: `0 10px 10px 10px` }} bgcolor={palette.contentBg} sx={{ marginTop: "0", padding: "20px", borderRadius: layout.borderRadius, display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                    <Box component="img" src={noRulesImg} width="200px" alt="No live classes going on!" />
                    <Typography variant='subtitle2' color="textSecondary" marginTop="10px">
                        No feature permission rules have been added yet!
                    </Typography>
                </Box>}

                {loading && !error && <DataLoadingSpinner padding={{ sm: "0 20px", xs: "0 10px" }} waitingMessage="Loading permissions please wait..." />}

                {error && <TryAgainMessageBlock getData={fetchAllPermissions} loading={loading} code={error.code} err={error.message} />}

                {/* Dialogs */}
                <AddFeaturePermissionDialog
                    dialogState={addDialogState}
                    setState={setAddDialogState}
                    refreshData={fetchAllPermissions}
                />
            </Box>
        ) : (
            <Box padding={layout.pagePadding} flexGrow={1}>
                <BlockMessage message="You do not have permission to manage features." type="error" />
            </Box>
        )
    )
} )

export default FeaturePermissions